// src/components/Dashboard/AlertsSection.jsx
import {
  ArrowForward,
  Close as CloseIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  TrendingDown,
  TrendingUp,
  Warning as WarningIcon
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography
} from '@mui/material';
import React, { useState } from 'react';
// import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import Sparkline from '../common/Sparkline';

const AlertCard = ({ alert, onClick, onViewDetails }) => {
  const getIconByType = (severity) => {
    switch (severity) {
      case 'HIGH':
        return <ErrorIcon color="error" />;
      case 'MEDIUM':
        return <WarningIcon color="warning" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const getTrendIndicator = (trend) => {
    if (!trend) return null;
    const firstValue = trend[0];
    const lastValue = trend[trend.length - 1];
    const isIncreasing = lastValue > firstValue;
    
    return isIncreasing ? (
      <TrendingUp color="error" />
    ) : (
      <TrendingDown color="success" />
    );
  };

  return (
    <Card
      onClick={onClick}
      sx={{ 
        cursor: 'pointer',
        '&:hover': { boxShadow: 6 },
        transition: 'box-shadow 0.3s'
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box display="flex" gap={1} alignItems="center">
            {getIconByType(alert.severity)}
            <Typography variant="h6">{alert.type}</Typography>
          </Box>
          {/* <Chip 
            label="!" 
            color={alert.severity === 'HIGH' ? 'error' : 'warning'}
            size="small"
          /> */}
        </Box>
        
        <Typography color="text.secondary" sx={{ mt: 2, mb: 2 }}>
          {alert.description}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Count</Typography>
            <Typography variant="h6">{alert.count}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Impact</Typography>
            <Typography variant="h6">{alert.impact}</Typography>
          </Grid>
        </Grid>

        {alert.trend && (
          <Box mt={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">Trend</Typography>
              {getTrendIndicator(alert.trend)}
            </Box>
            <Box height={40}>
              <Sparkline data={alert.trend.map(value => ({ value }))} />
            </Box>
          </Box>
        )}

        {alert.affected_services && (
          <Box mt={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2" color="text.secondary">
                Affected Services
              </Typography>
              <Button
                size="small"
                endIcon={<ArrowForward />}
                onClick={(e) => {
                  e.stopPropagation();
                  onViewDetails(alert);
                }}
              >
                View Details
              </Button>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap">
              {alert.affected_services.map((service, idx) => (
                <Chip key={idx} label={service} size="small" />
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const AlertsSection = ({ alerts }) => {
  const [selectedAlert, setSelectedAlert] = useState(null);

  const handleViewDetails = (alert) => {
    setSelectedAlert(alert);
  };

  // const alertsData = [
  //   { name: 'Critical', value: alerts.critical.length, color: '#d32f2f' },
  //   { name: 'Medium', value: alerts.medium.length, color: '#f57c00' },
  //   { name: 'Low', value: alerts.low.length, color: '#1976d2' }
  // ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" color="error">Critical Alerts</Typography>
        {/* <Box width={200} height={100}>
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={alertsData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={25}
                outerRadius={40}
              >
                {alertsData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Box> */}
      </Box>

      {alerts.critical.length > 0 && (
        <>

          <Grid container spacing={3} mb={4}>
            {alerts.critical.map((alert, idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <AlertCard 
                  alert={alert} 
                  onClick={() => setSelectedAlert(alert)}
                  onViewDetails={handleViewDetails}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Alert Detail Dialog */}
      <Dialog
        open={Boolean(selectedAlert)}
        onClose={() => setSelectedAlert(null)}
        maxWidth="md"
        fullWidth
      >
        {selectedAlert && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">{selectedAlert.type}</Typography>
                <IconButton onClick={() => setSelectedAlert(null)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="body1">{selectedAlert.description}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Impact</Typography>
                  <Typography variant="h6">{selectedAlert.impact}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Count</Typography>
                  <Typography variant="h6">{selectedAlert.count}</Typography>
                </Grid>
                {selectedAlert.trend && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Trend Analysis</Typography>
                    <Box height={200}>
                      <Sparkline 
                        data={selectedAlert.trend.map(value => ({ value }))}
                        height={200}
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedAlert(null)}>Close</Button>
              <Button variant="contained" color="primary">
                Take Action
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default AlertsSection;